import { COLORS, hexToPhaser } from "./colors";

export interface DailyStarsConfig {
  scene: Phaser.Scene;
  x: number;
  y: number;
  dailyExtractions: number;
  starSize?: number;
  starSpacing?: number;
  label?: string;
  labelFontSize?: string;
  labelColor?: string;
  animateAllStars?: boolean; // If true, animate all filled stars with stagger
  animateNewlyFilled?: boolean; // If true, animate only the newly filled star with elastic
  newlyFilledIndex?: number; // Index of the newly filled star (for animateNewlyFilled)
}

export class DailyStarsComponent {
  private container: Phaser.GameObjects.Container;
  private stars: Phaser.GameObjects.Image[] = [];
  private labelText?: Phaser.GameObjects.Text;

  constructor(config: DailyStarsConfig) {
    const {
      scene,
      x,
      y,
      dailyExtractions,
      starSize = 20,
      starSpacing = 2,
      label,
      labelFontSize = "14px",
      labelColor = COLORS.YELLOW,
      animateAllStars = false,
      animateNewlyFilled = false,
      newlyFilledIndex = -1,
    } = config;

    // Create container
    this.container = scene.add.container(x, y);
    this.container.setScrollFactor(0);

    // Add label if provided
    if (label) {
      this.labelText = scene.add
        .text(0, -28, label, {
          fontSize: labelFontSize,
          color: labelColor,
          fontFamily: "Anton",
          align: "center",
          stroke: COLORS.BLACK,
          strokeThickness: 2,
        })
        .setOrigin(0.5);
      this.labelText.setScrollFactor(0);
      this.container.add(this.labelText);
    }

    // Calculate star positions
    const totalStarsWidth = starSize * 5 + starSpacing * 4;
    const starsStartX = -totalStarsWidth / 2;

    // Create 5 stars
    for (let i = 0; i < 5; i++) {
      const starX = starsStartX + i * (starSize + starSpacing) + starSize / 2;
      const isFilled = i < dailyExtractions;

      // Settled scale for a star element sized to `starSize` and then shrunk 15% —
      // derived from setDisplaySize's own computed scale (relative to starSize) rather
      // than a flat 0.85 literal (which was 85% of the raw PNG's native pixel size
      // instead, making empty/background stars render bigger than filled ones).
      const getSettledScale = (image: Phaser.GameObjects.Image): number => {
        image.setDisplaySize(starSize, starSize);
        return image.scaleX * 0.85;
      };

      // For filled stars, add empty star background first
      if (isFilled) {
        const emptyBackgroundStar = scene.add.image(starX, 0, "star-empty");
        emptyBackgroundStar.setOrigin(0.5, 0.5);
        emptyBackgroundStar.setScale(getSettledScale(emptyBackgroundStar));
        emptyBackgroundStar.setDepth(-2); // Behind the yellow outline
        this.stars.push(emptyBackgroundStar);
        this.container.add(emptyBackgroundStar);
      }

      const starImage = scene.add.image(
        starX,
        0,
        isFilled ? "star-filled" : "star-empty",
      );
      starImage.setOrigin(0.5, 0.5);
      const settledScale = getSettledScale(starImage);

      // Add animation to filled stars
      if (isFilled) {
        if (animateNewlyFilled && i === newlyFilledIndex) {
          // Animate only the newly filled star with elastic effect
          starImage.setScale(0);
          scene.tweens.add({
            targets: starImage,
            scale: settledScale,
            duration: 500,
            ease: "Elastic.out",
            delay: 300,
            onComplete: () => {
              starImage.setScale(settledScale); // Ensure scale is exact at end
            },
          });
        } else if (animateAllStars) {
          // Animate all filled stars with domino effect (0.5s delay per filled star)
          starImage.setScale(0);
          // Calculate how many filled stars came before this one
          const filledStarIndex = i; // For filled stars, this is their position in the filled sequence
          scene.tweens.add({
            targets: starImage,
            scale: settledScale,
            duration: 300,
            delay: 1000 + i * 500, // 1 second base delay + 0.5 seconds per filled star
            ease: "Back.easeOut",
            onComplete: () => {
              starImage.setScale(settledScale); // Ensure scale is exact at end
            },
          });
        } else {
          starImage.setScale(settledScale);
        }
      } else {
        // Non-filled stars also at the settled scale for consistency
        starImage.setScale(settledScale);
      }

      // Apply yellow tint to reward stars (positions 0, 2, 4)
      if (i === 0 || i === 2 || i === 4) {
        starImage.setTint(hexToPhaser(COLORS.YELLOW));
      }

      starImage.setDepth(2); // Filled and empty stars on top
      this.stars.push(starImage);
      this.container.add(starImage);
    }
  }

  /**
   * Get the container
   */
  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Update the stars display
   */
  updateStars(dailyExtractions: number): void {
    for (let i = 0; i < this.stars.length; i++) {
      const isFilled = i < dailyExtractions;
      this.stars[i].setTexture(isFilled ? "star-filled" : "star-empty");
    }
  }

  /**
   * Set the depth of the container
   */
  setDepth(depth: number): void {
    this.container.setDepth(depth);
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    this.container.destroy();
    this.stars = [];
    this.labelText = undefined;
  }
}
